import {
  useGetSettings,
  usePatchSettings,
} from '@ipfabric/api-client/client/model/settings/settings';
import type { PatchSettingsBody } from '@ipfabric/api-client/client/schemas/patchSettingsBody';
import type { PatchSettingsBodySnapshotsAnyOfCronTimezone } from '@ipfabric/api-client/client/schemas/patchSettingsBodySnapshotsAnyOfCronTimezone';
import type { PatchSettingsBodySnapshotsRetentionActionCronTimezone } from '@ipfabric/api-client/client/schemas/patchSettingsBodySnapshotsRetentionActionCronTimezone';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';

import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { PageHeader } from '@ipfabric/ui-components/PageHeader/PageHeader';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { useNotifications } from 'utils/hooks/useNotifications';
import { renderQuery } from 'utils/react-query/renderQuery';
import { requestMessages } from 'utils/request/requestMessages';
import {
  SnapshotCollectionForm,
  snapshotCollectionFormId,
} from './SnapshotCollectionForm';
import {
  type SnapshotCollectionValues,
  prepareSnapshotCollectionValues,
} from './snapshotCollectionModel';

export const SnapshotCollectionPage = () => {
  const locale = useLocale();
  const messages = locale.snapshotCollection;
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const settingsQuery = useGetSettings(undefined, queryClient);
  const [isFormDirty, setFormDirty] = useState(false);
  const initialValues = useMemo(
    () =>
      settingsQuery.data
        ? prepareSnapshotCollectionValues({
            snapshots: settingsQuery.data.snapshots,
            snapshotsRetention: settingsQuery.data.snapshotsRetention,
          })
        : null,
    [
      settingsQuery.data?.snapshots,
      settingsQuery.data?.snapshotsRetention,
      settingsQuery.data,
    ],
  );

  const patchSettingsMutation = usePatchSettings(
    {
      mutation: {
        onSuccess: (data) => {
          queryClient.setQueryData(settingsQuery.queryKey, data);
          setFormDirty(false);
          notifications.showNotification({
            type: 'success',
            message: requestMessages.en.savedAdvancedSnapshots,
          });
        },
        onError: () => {
          notifications.showNotification({
            type: 'danger',
            message: 'Unable to save snapshot settings. Please try again.',
          });
        },
      },
    },
    queryClient,
  );

  const handleFormStateChange = useCallback(
    ({ isDirty }: { isDirty: boolean; cronValid: boolean }) => {
      setFormDirty(isDirty);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (values: SnapshotCollectionValues) => {
      const payload: PatchSettingsBody = {
        snapshots: values.snapshots.enabled
          ? {
              ...values.snapshots,
              cron: {
                expression: values.snapshots.cron.expression,
                timezone: values.snapshots.cron
                  .timezone as PatchSettingsBodySnapshotsAnyOfCronTimezone,
              },
            }
          : {
              enabled: values.snapshots.enabled,
              maxLoaded: values.snapshots.maxLoaded,
              maxLocked: values.snapshots.maxLocked,
            },
        snapshotsRetention: {
          all: values.snapshotsRetention.all,
          firstOfDay: values.snapshotsRetention.firstOfDay,
          firstOfWeek: values.snapshotsRetention.firstOfWeek,
          firstOfMonth: values.snapshotsRetention.firstOfMonth,
          firstOfYear: values.snapshotsRetention.firstOfYear,
          removalMetrics: values.snapshotsRetention.removalMetrics,
          action: {
            command: values.snapshotsRetention.action.command,
            cron: {
              expression: values.snapshotsRetention.action.cron.expression,
              timezone: values.snapshotsRetention.action.cron
                .timezone as PatchSettingsBodySnapshotsRetentionActionCronTimezone,
            },
          },
        },
      };

      try {
        await patchSettingsMutation.mutateAsync({ data: payload });
      } catch {
        // Error notification handled in onError.
      }
    },
    [patchSettingsMutation],
  );

  const renderPendingState = useCallback(
    () => (
      <div className='box box-solid mb-0 flex flex-1 items-center'>
        <LoaderAndText />
      </div>
    ),
    [],
  );

  const renderErrorState = useCallback(
    () => (
      <div className='box box-solid mb-0 flex flex-1 items-center'>
        <LoaderAndText
          hasError
          errorText='Unable to load snapshot settings. Please try again.'
        />
      </div>
    ),
    [],
  );

  return renderQuery({
    query: settingsQuery,
    renderStaleDataOnMount: false,
    pending: renderPendingState,
    error: renderErrorState,
    success: (_data) => {
      if (initialValues === null) {
        stumble('SnapshotCollectionPage: Data from settingsQuery is null');
        return 'No data available';
      }
      return (
        <div className='flex flex-col items-center'>
          <Helmet title={messages.title} />
          <PageHeader
            elevated
            width='content-default'
            label={messages.title}
            buttons={
              <Button
                type='submit'
                form={snapshotCollectionFormId}
                variant='primary'
                icon='fa-save'
                disabled={!isFormDirty || patchSettingsMutation.isPending}
                loading={patchSettingsMutation.isPending}
              >
                {messages.btnSave}
              </Button>
            }
          />
          <div className='w-full max-w-content-default flex flex-col px my gap'>
            <SnapshotCollectionForm
              initialValues={initialValues}
              onSubmit={handleSubmit}
              submitting={patchSettingsMutation.isPending}
              messages={messages}
              onFormStateChange={handleFormStateChange}
            />
          </div>
        </div>
      );
    },
  });
};
